import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { OepaUserAccount } from '@ohioepa/oepa-common-lib';
import { OepaIopAuthLibService } from '@ohioepa/oepa-iop-auth-lib';
import { OepaUserAccountService } from '@ohioepa/oepa-ui-lib';
import { ToastrService } from 'ngx-toastr';
import { intLiveReloadAccount } from './int-live-reload-account';
import { extLiveReloadAccount } from './ext-live-reload-account';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'OEPA_swims_ui';

  constructor(private route: ActivatedRoute, private router: Router,
    private authService: OepaIopAuthLibService,
    private toastr: ToastrService,
    private userAccountService: OepaUserAccountService) {
  }

  ngOnInit() {
    this.getEbizVersion();
    this.route.queryParams.subscribe((params: Params) => {
      this.getQueryParameters(params);
      let loginInitialized = sessionStorage.getItem('LOGIN_INITIALIZED');
      if (!loginInitialized) {
        this.loadApp(params);
      }
      if (params['code']) {
        this.login();
      }
    });
  }

  private initalLogin() {
    this.authService.configureOAuth();
    setTimeout(() => {
      this.authService.login();
      sessionStorage.setItem('LOGIN_INITIALIZED', 'true');
    }, 1000)
  }

  async login() {
    this.authService.configureOAuth();
    setTimeout(async () => {
      let authenticated: Promise<boolean> = this.authService.tryLogin();
      if (await authenticated) {
        this.retrieveUserInfo();
      }
    }, 500);
  }

  private retrieveIOPUserAccount(userID: string) {
    this.userAccountService.setAuthToken(this.authService.getAccessToken());
    this.userAccountService.retrieveAccountForUser(userID, 'SWIMS').subscribe({
      next: (account: OepaUserAccount) => {
        if (account) {
          this.initializeUserAccount(account);
        } else {
          console.log('No account found');
        }
      }, error: err => {
        this.toastr.error('Initialization error: ' + err, 'ERROR');
      }
    });
  }
  
    private retrieveLiveReloadAccount() {
      let liveReloadAccount = this.getApp() === 'swims' ? intLiveReloadAccount : extLiveReloadAccount;
        let overrrideUserID = this.getUserID();
        if (overrrideUserID) {
          liveReloadAccount.loginName = overrrideUserID;
          liveReloadAccount.accountID = overrrideUserID;
        }
      this.userAccountService.setLiveReloadUser(liveReloadAccount).subscribe(() => {
          this.userAccountService.retrieveAccountForCurrentUser('SWIMS').subscribe({
            next: (account: OepaUserAccount) => {
              if (account) {
                this.initializeUserAccount(account);
                this.userAccountService.userAccount$.next({});
              } else {
                console.log('No account found');
              }
            }, error: err => {
              this.toastr.error('Initialization error: ' + err, 'ERROR');
            }
          });
      });
    }

  private retrieveEBizUserAccount(userID: string) {
    this.userAccountService.retrieveAccountForUser(userID, 'SWIMS').subscribe({
      next: (account: OepaUserAccount) => {
        if (account) {
          this.initializeUserAccount(account);
        } else {
          console.log('No account found');
        }
      }, error: err => {
        this.toastr.error('Initialization error: ' + err, 'ERROR');
      }
    });
  }

  private initializeUserAccount(account: OepaUserAccount) {
    if (account) {
      this.userAccountService.setCurrentUserAccount(account);
      this.routeToApplication();
    } else {
      console.log('No account found');
    }
  }

  retrieveUserInfo() {
    this.authService.userProfile$.subscribe((profile) => {
      this.retrieveIOPUserAccount(this.getUserID());
    });
  }

  private loadApp(params: Params) {
    if (this.isLocalDevelopment()) {
      if (params && params['app']) {
        this.retrieveLiveReloadAccount();
      }
    } else {
      if (this.getLoginParam()) {
        this.retrieveEBizUserAccount(this.getLoginParam());
      } else if (this.getUserID()) {
        let loginInitialized = sessionStorage.getItem('LOGIN_INITIALIZED');
        if (!loginInitialized) {
          this.initalLogin();
        }
      }
    }
  }

  private isLocalDevelopment(): boolean {
    return (window.location.href.indexOf('localhost') > -1 && window.location.href.indexOf('7001') < 0);
  }

  private getQueryParameters(params: Params) {
    if (params['app']) {
      this.setApp(params['app']);
    }
    if (params['uid']) {
      this.setUserID(params['uid']);
    }
    if (params['login']) {
      this.setLoginParam(params['login']);
    }
    if (params['reload']) {
      sessionStorage.removeItem('LOGIN_INITIALIZED');
    }
  }

  ngOnDestroy(): void {
    sessionStorage.clear();
  }

  private routeToApplication() {
    this.router.navigate([this.getApp() || 'streams']);
  }

  private setApp(app: string) {
    sessionStorage.setItem('app', app);
  }

  private getApp(): string {
    return sessionStorage.getItem('app') || '';
  }

  private setUserID(userID: string) {
    sessionStorage.setItem('USER_ID', userID);
  }

  private getUserID(): string {
    return sessionStorage.getItem('USER_ID') || '';
  }

  private setLoginParam(loginParam: string) {
    sessionStorage.setItem('LOGIN_PARAM', loginParam);
  }

  private getLoginParam(): string {
    return sessionStorage.getItem('LOGIN_PARAM') || '';
  }

  private getEbizVersion() {
    this.userAccountService.getEBizVersion('SWIMS').subscribe(ebizVersion => {
      if (ebizVersion) {
        sessionStorage.setItem('EBIZ_VERSION', ebizVersion);
      }
    })
  }
}
